import {
  resolveModelSettingPayload,
  resolveTaskDetailPayload,
  supportsArProjection
} from './render-task-contract.js'
import { ApiServer, configureTaskService } from './task-service.js'

const DEFAULT_API_BASE = 'https://nj.metop.com.cn/backend'

function joinUrl(base, path) {
  return `${String(base).replace(/\/+$/, '')}/${String(path).replace(/^\/+/, '')}`
}

function readError(payload, status) {
  const error = payload && (payload.message || payload.error_message || payload.error)
  return typeof error === 'string' ? error : error?.message || `请求失败（${status}）`
}

function normalizeUploadedAnnotationAttachment(result, file) {
  const sourceFile = file && typeof file === 'object' ? file : {}
  const payload = result?.data ?? result ?? {}
  const url = String(
    payload.url || payload.fileUrl || payload.file_url || payload.signedUrl || payload.signed_url ||
    payload.downloadUrl || payload.download_url || ''
  ).trim()
  const fileId = String(payload.fileId || payload.file_id || payload.id || '').trim()
  if (!url || !fileId) {
    throw new Error(!url ? '附件上传成功但未返回访问地址' : '附件上传成功但未返回文件引用')
  }
  return {
    fileId,
    storageKey: payload.storageKey || payload.storage_key || payload.key || undefined,
    name: String(payload.name || payload.fileName || payload.file_name || sourceFile.name || 'attachment'),
    size: Number(payload.size ?? sourceFile.size) || Number(sourceFile.size) || 0,
    mime: String(payload.mime || payload.contentType || payload.content_type || sourceFile.type || 'application/octet-stream').toLowerCase(),
    url
  }
}

function readContentLength(response) {
  const contentLength = Number(response.headers.get('content-length') || 0)
  if (Number.isFinite(contentLength) && contentLength > 0) return contentLength
  const contentRange = response.headers.get('content-range') || ''
  const rangeTotal = Number(contentRange.match(/\/(\d+)\s*$/)?.[1] || 0)
  return Number.isFinite(rangeTotal) && rangeTotal > 0 ? rangeTotal : 0
}

async function readBinaryResponse(response, onProgress) {
  const total = readContentLength(response)
  const report = loaded => {
    const percent = total > 0 ? Math.min(100, loaded / total * 100) : null
    onProgress?.({ loaded, total, percent })
  }

  report(0)
  if (!response.body?.getReader) {
    const buffer = await response.arrayBuffer()
    onProgress?.({
      loaded: buffer.byteLength,
      total: total || buffer.byteLength,
      percent: 100
    })
    return buffer
  }

  const reader = response.body.getReader()
  const chunks = []
  let loaded = 0
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    chunks.push(value)
    loaded += value.byteLength
    report(loaded)
  }

  const merged = new Uint8Array(loaded)
  let offset = 0
  for (const chunk of chunks) {
    merged.set(chunk, offset)
    offset += chunk.byteLength
  }
  onProgress?.({ loaded, total: total || loaded, percent: 100 })
  return merged.buffer
}

export function createSseParser(onEvent) {
  let buffer = ''

  const parseBlock = block => {
    let event = 'message'
    const dataLines = []
    for (const line of block.split(/\r\n|\n|\r/)) {
      if (!line || line.startsWith(':')) continue
      if (line.startsWith('event:')) event = line.slice(6).trim()
      if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart())
    }
    if (!dataLines.length) return
    const rawData = dataLines.join('\n')
    let data
    try {
      data = JSON.parse(rawData)
    } catch {
      throw new Error('AI 流数据解析失败')
    }
    onEvent(event, data)
  }

  const drain = flush => {
    let separator = buffer.match(/\r\n\r\n|\n\n|\r\r/)
    while (separator) {
      const index = separator.index || 0
      const block = buffer.slice(0, index)
      buffer = buffer.slice(index + separator[0].length)
      if (block) parseBlock(block)
      separator = buffer.match(/\r\n\r\n|\n\n|\r\r/)
    }
    if (flush && buffer) {
      parseBlock(buffer)
      buffer = ''
    }
  }

  return {
    push(text) {
      buffer += String(text || '')
      drain(false)
    },
    finish() {
      drain(true)
    }
  }
}

export class CommunityApi {
  constructor(baseUrl = DEFAULT_API_BASE, authToken = '', options = {}) {
    this.baseUrl = String(baseUrl || DEFAULT_API_BASE).replace(/\/+$/, '')
    this.miniappClientId = String(options.miniappClientId || '').trim()
    this.authToken = authToken
    this.project = Boolean(options.project)
    configureTaskService(this)
  }

  headers(json = false, authToken = this.authToken) {
    return {
      Accept: 'application/json',
      ...(json ? { 'Content-Type': 'application/json' } : {}),
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
    }
  }

  async request(path, options = {}) {
    const { skipAuth = false, timeout = 0, signal, ...fetchOptions } = options
    let controller = null
    let timeoutId = null
    let removeAbortListener = () => {}
    if (typeof AbortController !== 'undefined' && Number(timeout) > 0) {
      controller = new AbortController()
      if (signal) {
        if (signal.aborted) controller.abort()
        else {
          const forwardAbort = () => controller.abort()
          signal.addEventListener('abort', forwardAbort, { once: true })
          removeAbortListener = () => signal.removeEventListener('abort', forwardAbort)
        }
      }
      timeoutId = setTimeout(() => controller.abort(), Number(timeout))
      fetchOptions.signal = controller.signal
    } else if (signal) {
      fetchOptions.signal = signal
    }
    try {
      const response = await fetch(joinUrl(this.baseUrl, path), {
        ...fetchOptions,
        headers: { ...this.headers(Boolean(options.body), skipAuth ? '' : this.authToken), ...(options.headers || {}) }
      })
      const payload = await response.json().catch(() => null)
      if (!response.ok) {
        const error = new Error(readError(payload, response.status))
        error.statusCode = response.status
        error.code = payload?.code || payload?.error?.code || ''
        throw error
      }
      return payload
    } catch (error) {
      if (controller?.signal.aborted && !signal?.aborted) {
        const timeoutError = new Error('请求超时，请检查网络后重试')
        timeoutError.code = 'TIMEOUT'
        throw timeoutError
      }
      throw error
    } finally {
      if (timeoutId) clearTimeout(timeoutId)
      removeAbortListener()
    }
  }

  async getTask(taskId, activity = '', shareId = '') {
    const path = this.project
      ? `/api/task/${encodeURIComponent(taskId)}`
      : shareId
        ? `/api/task/${encodeURIComponent(taskId)}`
      : `/api/community/task/${encodeURIComponent(taskId)}${activity ? `?activity=${encodeURIComponent(activity)}&channel=miniapp` : ''}`
    const payload = await this.request(
      path + (shareId ? `${path.includes('?') ? '&' : '?'}shareId=${encodeURIComponent(shareId)}` : ''),
      shareId ? { skipAuth: true } : {}
    )
    return resolveTaskDetailPayload(payload) || {}
  }

  async getSetting(taskId, activity = '', shareId = '') {
    const encodedTaskId = encodeURIComponent(taskId)
    const communityPath = `/api/community/getModelSetting/${encodedTaskId}${activity ? `?activity=${encodeURIComponent(activity)}&channel=miniapp` : ''}`
    const projectPath = `/api/getModelSetting/${encodedTaskId}`
    // A share id grants read access to the task itself, including its saved
    // initial camera pose. Private projects are not visible through the
    // community settings endpoint, so shared previews must use the canonical
    // project settings endpoint as well.
    if (this.project || shareId) {
      return resolveModelSettingPayload(await this.request(projectPath, shareId ? { skipAuth: true } : {}))
    }
    const communitySetting = resolveModelSettingPayload(await this.request(communityPath)) || {}
    // The public endpoint on older deployments may omit the saved initial
    // pose. When the viewer has the owner's login token, use the canonical
    // project setting as a fallback so every entry point starts identically.
    if (!communitySetting.initialCameraPose && this.authToken) {
      try {
        const ownerSetting = resolveModelSettingPayload(await this.request(projectPath))
        if (ownerSetting?.initialCameraPose) return ownerSetting
      } catch {
        // Public viewing must continue with the community setting/default.
      }
    }
    return communitySetting
  }

  async saveSetting(taskId, setting, activity = '') {
    if (!this.project) throw new Error('社区模型为只读模式，不能修改初始视角')
    if (!this.authToken) throw new Error('请先登录后再设置初始视角')
    return this.request('/api/saveModelSetting', {
      method: 'POST',
      body: JSON.stringify({
        task_id: taskId,
        ...(activity ? { activity } : {}),
        setting: setting && typeof setting === 'object' ? setting : {}
      })
    })
  }

  async getCameraData(taskId, activity = '', shareId = '') {
    const sharedQuery = shareId ? `&shareId=${encodeURIComponent(shareId)}` : ''
    const path = this.project || shareId
      ? `/api/camera/getData?task_id=${encodeURIComponent(taskId)}${sharedQuery}`
      : `/api/community/camera/getData?task_id=${encodeURIComponent(taskId)}${activity ? `&activity=${encodeURIComponent(activity)}&channel=miniapp` : ''}`
    if (activity && this.authToken) {
      try {
        const ownerPayload = await this.request(`/api/camera/getData?task_id=${encodeURIComponent(taskId)}`)
        return ownerPayload && (ownerPayload.data || ownerPayload) || null
      } catch {
        // Activity/community camera data remains the public fallback.
      }
    }
    const payload = await this.request(path, shareId ? { skipAuth: true } : {})
    return payload && (payload.data || payload) || null
  }

  async getAnnotations(taskId, activity = '') {
    const payload = await this.request(`/api/task/getAnnotation?taskId=${encodeURIComponent(taskId)}${activity ? `&activity=${encodeURIComponent(activity)}&channel=miniapp` : ''}`)
    const data = payload?.data?.data ?? payload?.data ?? payload
    return Array.isArray(data) ? data : []
  }

  async saveAnnotations(taskId, data, uploadSessionId = '', activity = '') {
    return this.request('/api/task/createAnnotation', {
      method: 'POST',
      body: JSON.stringify({
        taskId,
        ...(activity ? { activity } : {}),
        data: Array.isArray(data) ? data : [],
        ...(uploadSessionId ? { uploadSessionId } : {})
      })
    })
  }

  async uploadAnnotationAttachment(taskId, file, { uploadSessionId = '', onProgress, signal } = {}) {
    if (!file || typeof file !== 'object' || typeof file.size !== 'number' || !file.name) {
      throw new Error('附件文件无效，请重新选择附件')
    }
    // Some embedded WebViews do not expose Node-compatible globals that the
    // browser build of AWS SDK v2 probes during module initialization.
    if (typeof globalThis !== 'undefined') {
      if (typeof globalThis.global === 'undefined') globalThis.global = globalThis
      if (typeof globalThis.process === 'undefined') globalThis.process = { env: {} }
      if (typeof globalThis.Buffer === 'undefined') {
        try {
          const bufferModule = await import('buffer')
          if (bufferModule?.Buffer) globalThis.Buffer = bufferModule.Buffer
        } catch (error) {
          console.warn('[MeTop viewer] Buffer polyfill unavailable', error)
        }
      }
    }
    let directUpload
    let eosUploaderReady = false
    const decorateEosError = error => {
      const code = String(error?.code || '').trim()
      const status = Number(error?.statusCode || 0)
      const stack = String(error?.stack || '').split('\n').slice(1, 3).join(' | ').trim()
      const detail = [code && `code=${code}`, status && `status=${status}`, stack && `stack=${stack}`].filter(Boolean).join(', ')
      if (error && typeof error === 'object') {
        error.code = code || error.code || 'EOS_UPLOAD_FAILED'
        error.message = `EOS 附件上传失败${detail ? `（${detail}）` : ''}：${error.message || '未知错误'}`
      }
      return error
    }
    try {
      const { configureEosApiServer, uploadAnnotationAttachmentDirect } = await import('./eos-annotation-upload.js')
      // Inject the already configured request adapter.  The EOS module must not
      // import task-service itself: doing so makes Vite emit a cycle between the
      // entry chunk and the lazy EOS chunk, which breaks WebView module setup.
      configureEosApiServer(ApiServer)
      eosUploaderReady = true
      directUpload = await uploadAnnotationAttachmentDirect({
          taskId,
          file,
          annotationUploadSessionId: uploadSessionId,
          onProgress,
          signal
        })
    } catch (error) {
      // A stale WebView may still fail while evaluating the lazy chunk.  Keep
      // the legacy endpoint available in that case; actual EOS request errors
      // are surfaced with their code/status/stack instead of being hidden.
      if (eosUploaderReady) throw decorateEosError(error)
      console.warn('[MeTop viewer] EOS uploader module failed to initialize', decorateEosError(error))
      directUpload = null
    }
    if (directUpload) {
      return normalizeUploadedAnnotationAttachment(directUpload.attachment, file)
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      const form = new FormData()
      form.append('taskId', taskId)
      if (uploadSessionId) form.append('uploadSessionId', uploadSessionId)
      form.append('file', file, file.name)
      const abort = () => xhr.abort()
      if (signal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'))
        return
      }
      signal?.addEventListener('abort', abort, { once: true })
      xhr.open('POST', joinUrl(this.baseUrl, '/api/task/uploadAnnotationAttachment'))
      // Do not leave a legacy attachment upload pending forever when the
      // backend or network stops responding.
      xhr.timeout = 5 * 60 * 1000
      xhr.setRequestHeader('Accept', 'application/json')
      if (this.authToken) xhr.setRequestHeader('Authorization', `Bearer ${this.authToken}`)
      xhr.upload.onprogress = event => {
        if (!event.lengthComputable || event.total <= 0) return
        onProgress?.(Math.max(0, Math.min(100, Math.round(event.loaded / event.total * 100))))
      }
      xhr.onerror = () => reject(new Error('附件上传失败，请检查网络'))
      xhr.ontimeout = () => {
        const error = new Error('附件上传超时，请检查网络后重试')
        error.code = 'UPLOAD_TIMEOUT'
        reject(error)
      }
      xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'))
      xhr.onload = () => {
        let body = null
        try {
          body = JSON.parse(xhr.responseText || 'null')
        } catch {
          // The status-based fallback below is more useful than a JSON parse error.
        }
        if (xhr.status < 200 || xhr.status >= 300) {
          const error = new Error(readError(body, xhr.status))
          error.statusCode = xhr.status
          error.code = body?.code || body?.error?.code || ''
          reject(error)
          return
        }
        try {
          resolve(normalizeUploadedAnnotationAttachment(body, file))
        } catch (error) {
          reject(error)
        }
      }
      xhr.onloadend = () => signal?.removeEventListener('abort', abort)
      xhr.send(form)
    })
  }

  async uploadRecordedVideo(taskId, blob, filename, { onProgress } = {}) {
    if (!this.authToken) throw new Error('请先登录后保存视频')
    if (!blob || typeof blob.size !== 'number' || blob.size <= 0) throw new Error('录制视频文件无效')
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      const form = new FormData()
      form.append('taskId', String(taskId || ''))
      form.append('video', blob, String(filename || 'metop-recording.mp4'))
      xhr.open('POST', joinUrl(this.baseUrl, '/api/miniapp/recorded-video'))
      xhr.timeout = 5 * 60 * 1000
      xhr.setRequestHeader('Accept', 'application/json')
      xhr.setRequestHeader('Authorization', `Bearer ${this.authToken}`)
      xhr.upload.onprogress = event => {
        if (!event.lengthComputable || event.total <= 0) return
        onProgress?.(Math.max(0, Math.min(100, event.loaded / event.total * 100)))
      }
      xhr.onerror = () => reject(new Error('视频上传失败，请检查网络'))
      xhr.ontimeout = () => reject(new Error('视频上传超时，请检查网络后重试'))
      xhr.onload = () => {
        let body = null
        try {
          body = JSON.parse(xhr.responseText || 'null')
        } catch {
          // Use the status fallback below when a proxy returns non-JSON text.
        }
        if (xhr.status < 200 || xhr.status >= 300) {
          const error = new Error(readError(body, xhr.status))
          error.statusCode = xhr.status
          reject(error)
          return
        }
        const payload = body?.data ?? body ?? {}
        const url = String(payload.url || payload.downloadUrl || payload.download_url || '').trim()
        if (!/^https:\/\//i.test(url)) {
          reject(new Error('视频上传成功但未返回安全下载地址'))
          return
        }
        onProgress?.(100)
        resolve({
          url,
          filename: String(payload.filename || filename || 'metop-recording.mp4'),
          expiresAt: Number(payload.expiresAt || payload.expires_at || 0)
        })
      }
      xhr.send(form)
    })
  }

  async getImageVideoAnalysisVersion({ signal } = {}) {
    if (!this.authToken) throw new Error('请先登录后生成视频')
    if (!this.miniappClientId) throw new Error('缺少小程序客户端标识')
    const query = `?client_id=${encodeURIComponent(this.miniappClientId)}`
    const response = await fetch(joinUrl(this.baseUrl, `/api/miniapp/image-video/analysis-version${query}`), {
      method: 'GET',
      headers: {
        ...this.headers(false),
        'X-Miniapp-Client': this.miniappClientId
      },
      signal
    })
    const payload = await response.json().catch(() => null)
    if (!response.ok || payload?.success === false) {
      const error = new Error(readError(payload, response.status) || '查询视频版本失败')
      error.statusCode = response.status
      error.code = payload?.code || payload?.error?.code || ''
      throw error
    }
    const source = payload?.data && typeof payload.data === 'object' ? payload.data : payload
    const version = String(source?.version || '').trim()
    const revision = Number(source?.revision)
    if (!version) throw new Error('视频版本接口未返回有效版本')
    return { version, revision: Number.isFinite(revision) ? revision : null }
  }

  async createImageVideoTask(image, filename = 'metop-initial-view.jpg', { signal, modelTaskId, generationVersion } = {}) {
    if (!this.authToken) throw new Error('请先登录后生成视频')
    if (!image || typeof image.size !== 'number' || image.size <= 0) throw new Error('初始视角截图无效')
    const form = new FormData()
    form.append('image', image, String(image.name || filename))
    if (modelTaskId) form.append('task_id', String(modelTaskId))
    if (generationVersion) form.append('version', String(generationVersion))
    const response = await fetch(joinUrl(this.baseUrl, '/api/miniapp/image-video-tasks'), {
      method: 'POST',
      headers: this.headers(false),
      body: form,
      signal
    })
    const payload = await response.json().catch(() => null)
    if (!response.ok || payload?.success === false) {
      const error = new Error(readError(payload, response.status) || '创建视频失败')
      error.statusCode = response.status
      error.code = payload?.code || payload?.error?.code || ''
      error.payload = payload
      throw error
    }
    const task = payload?.data ?? payload ?? {}
    const taskId = String(task.task_id || task.taskId || task.id || '').trim()
    if (!taskId) throw new Error('视频任务创建成功但未返回任务 ID')
    return { ...task, task_id: taskId }
  }

  async getImageVideoTaskByModel(modelTaskId, { signal } = {}) {
    const id = String(modelTaskId || '').trim()
    if (!this.authToken) throw new Error('请先登录后查询视频')
    if (!id) throw new Error('缺少模型任务 ID')
    const response = await fetch(joinUrl(this.baseUrl, `/api/miniapp/image-video-tasks/by-model/${encodeURIComponent(id)}`), {
      method: 'GET',
      headers: this.headers(false),
      signal
    })
    const payload = await response.json().catch(() => null)
    if (response.status === 404 && payload?.code === 'IMAGE_VIDEO_TASK_NOT_FOUND') return null
    if (!response.ok || payload?.success === false) {
      const error = new Error(readError(payload, response.status))
      error.statusCode = response.status
      error.code = payload?.code || payload?.error?.code || ''
      error.payload = payload
      throw error
    }
    const task = payload?.data ?? payload ?? {}
    const taskId = String(task.task_id || task.taskId || task.id || '').trim()
    if (!taskId && String(task.status || '').toLowerCase() !== 'failed') {
      throw new Error('模型视频查询未返回任务 ID')
    }
    return { ...task, task_id: taskId }
  }

  async getImageVideoTask(taskId, { signal } = {}) {
    const normalizedTaskId = String(taskId || '').trim()
    if (!this.authToken) throw new Error('请先登录后查询视频')
    if (!normalizedTaskId) throw new Error('缺少视频任务 ID')
    const response = await fetch(joinUrl(this.baseUrl, `/api/miniapp/image-video-tasks/${encodeURIComponent(normalizedTaskId)}`), {
      method: 'GET',
      headers: this.headers(false),
      signal
    })
    const payload = await response.json().catch(() => null)
    if (!response.ok || payload?.success === false) {
      const error = new Error(readError(payload, response.status) || '查询视频任务失败')
      error.statusCode = response.status
      error.code = payload?.code || payload?.error?.code || ''
      error.payload = payload
      throw error
    }
    const task = payload?.data ?? payload ?? {}
    return { ...task, task_id: String(task.task_id || task.taskId || task.id || normalizedTaskId) }
  }

  async cancelAnnotationAttachmentUpload(taskId, uploadSessionId) {
    if (!taskId || !uploadSessionId) return
    await this.request('/api/task/cancelAnnotationAttachmentUpload', {
      method: 'POST',
      body: JSON.stringify({ taskId, uploadSessionId })
    })
  }

  async like(taskId) {
    if (!this.authToken) throw new Error('请先登录后点赞')
    return this.request('/api/likes/like', {
      method: 'POST',
      body: JSON.stringify({ task_id: taskId, taskId })
    })
  }

  async getDownloadToken(taskId, activity = '', shareId = '') {
    let tokenEndpoint
    if (this.project) {
      tokenEndpoint = `/api/task/${encodeURIComponent(taskId)}/download-token`
    } else if (activity) {
      // Activity models may keep tasks.is_public=false. The activity-scoped
      // miniapp endpoint checks the activity/model binding instead.
      tokenEndpoint = `/api/miniapp/download-token?task_id=${encodeURIComponent(taskId)}&activity=${encodeURIComponent(activity)}`
    } else {
      tokenEndpoint = shareId
        ? `/api/task/${encodeURIComponent(taskId)}/download-token?shareId=${encodeURIComponent(shareId)}`
        : `/api/task/${encodeURIComponent(taskId)}/public-download-token`
    }
    const payload = await this.request(tokenEndpoint, {
      method: 'POST',
      body: JSON.stringify({ purpose: 'view' }),
      ...(shareId ? { skipAuth: true } : {})
    })
    if (!payload || !payload.token) throw new Error('未获取到模型下载凭证')
    return payload.token
  }

  async downloadModel(taskId, format, onProgress, activity = '', shareId = '') {
    onProgress?.({ loaded: 0, total: 0, percent: null, requesting: true })
    const token = await this.getDownloadToken(taskId, activity, shareId)
    const shareQuery = shareId ? `&shareId=${encodeURIComponent(shareId)}` : ''
    const url = joinUrl(this.baseUrl, `/api/model/${encodeURIComponent(taskId)}?format=${encodeURIComponent(format)}&token=${encodeURIComponent(token)}${shareQuery}`)
    // The public Web viewer uses publicTransferRequest, which strips the
    // user's Authorization header before following a model redirect to EOS.
    // Keep the same behavior for community/activity/share entries; only an
    // owner project download needs the login token on this transfer.
    const response = await fetch(url, {
      headers: this.project ? this.headers(false) : { Accept: 'application/json' }
    })
    if (!response.ok) {
      const payload = await response.json().catch(() => null)
      const error = new Error(readError(payload, response.status))
      error.statusCode = response.status
      throw error
    }
    return readBinaryResponse(response, onProgress)
  }
}

export class AiChatApi {
  constructor(baseUrl = DEFAULT_API_BASE, authToken = '', channel = 'web') {
    this.baseUrl = String(baseUrl || DEFAULT_API_BASE).replace(/\/+$/, '')
    this.authToken = String(authToken || '').trim()
    this.channel = channel === 'miniapp' ? 'miniapp' : 'web'
  }

  async stream({ message, guideTaskId = '', channel = this.channel, images = [], signal, onStart, onReasoning, onDelta, onDone, onError } = {}) {
    const prompt = String(message || '').trim()
    const taskId = String(guideTaskId || '').trim()
    if (!prompt) throw new Error('请输入导游提示词')
    if (!taskId) throw new Error('缺少模型任务 ID')
    const form = new FormData()
    form.append('message', prompt)
    form.append('guide_task_id', taskId)
    form.append('channel', channel === 'miniapp' ? 'miniapp' : 'web')
    Array.from(images || []).filter(Boolean).slice(0, 3).forEach((image, index) => {
      form.append('images', image, String(image.name || `metop-view-${index + 1}.jpg`))
    })
    const response = await fetch(joinUrl(this.baseUrl, '/api/chat/completions'), {
      method: 'POST',
      headers: {
        Accept: 'text/event-stream',
        ...(this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {})
      },
      body: form,
      signal
    })
    if (!response.ok) {
      const payload = await response.json().catch(() => null)
      const error = new Error(readError(payload, response.status))
      error.statusCode = response.status
      error.code = payload?.code || payload?.error?.code || ''
      error.requestId = payload?.request_id || ''
      throw error
    }
    if (!response.body) throw new Error('当前浏览器不支持流式响应')
    const contentType = response.headers.get('content-type') || ''
    if (!contentType.toLowerCase().includes('text/event-stream')) {
      await response.body.cancel().catch(() => {})
      throw new Error('AI 接口未返回流式响应')
    }
    const reader = response.body.getReader()
    const decoder = new TextDecoder('utf-8')
    let answer = ''
    let completed = false
    let doneMeta = null
    const parser = createSseParser((event, data) => {
      if (event === 'start') { onStart?.(data); return }
      if (event === 'reasoning_delta') { onReasoning?.(String(data?.content || ''), data); return }
      if (event === 'delta') {
        const content = String(data?.content || '')
        answer += content
        onDelta?.(content, data)
        return
      }
      if (event === 'done') { completed = true; doneMeta = data; onDone?.(data); return }
      if (event === 'error') {
        const error = new Error(data?.message || 'AI 导游失败')
        error.code = data?.code || 'CHAT_STREAM_ERROR'
        error.requestId = data?.request_id || ''
        onError?.(data)
        throw error
      }
    })
    try {
      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        parser.push(decoder.decode(value, { stream: true }))
      }
      parser.push(decoder.decode())
      parser.finish()
      if (!completed) throw new Error('AI 响应意外中断，请重试')
      if (!answer.trim()) throw new Error('AI 暂未返回有效内容')
      return { answer, meta: doneMeta }
    } catch (error) {
      await reader.cancel().catch(() => {})
      throw error
    } finally {
      reader.releaseLock()
    }
  }
}

export class ModelGuideApi {
  constructor(baseUrl = DEFAULT_API_BASE, authToken = '', channel = 'web') {
    this.baseUrl = String(baseUrl || DEFAULT_API_BASE).replace(/\/+$/, '')
    this.authToken = String(authToken || '').trim()
    this.channel = channel === 'miniapp' ? 'miniapp' : 'web'
  }

  buildQuery({ shareId = '', activity = '', channel = this.channel } = {}) {
    const params = new URLSearchParams()
    const normalizedShareId = String(shareId || '').trim()
    const normalizedActivity = String(activity || '').trim()
    if (normalizedShareId) params.set('shareId', normalizedShareId)
    if (normalizedActivity) {
      params.set('activity', normalizedActivity)
      if (channel === 'miniapp') params.set('channel', 'miniapp')
    }
    const query = params.toString()
    return query ? `?${query}` : ''
  }

  async getModelGuide(taskId, { shareId = '', activity = '', channel = this.channel, signal } = {}) {
    const id = String(taskId || '').trim()
    if (!id) throw new Error('缺少模型任务 ID')
    const query = this.buildQuery({ shareId, activity, channel })
    const response = await fetch(joinUrl(this.baseUrl, `/api/task/${encodeURIComponent(id)}/ai-guide${query}`), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        ...(this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {})
      },
      signal
    })
    const payload = await response.json().catch(() => null)
    const data = payload?.data && typeof payload.data === 'object' ? payload.data : payload || {}
    if (response.status === 202 || data.status === 'generating') {
      return {
        status: 'generating',
        taskId: String(data.task_id || id),
        revision: Number.isFinite(Number(data.revision)) ? Number(data.revision) : null,
        retryAfterMs: Math.max(100, Math.min(5000, Number(data.retry_after_ms) || 500))
      }
    }
    if (!response.ok) {
      const error = new Error(readError(payload, response.status))
      error.statusCode = response.status
      error.code = payload?.code || payload?.error?.code || ''
      error.requestId = payload?.request_id || ''
      throw error
    }
    const content = String(data.content || '').trim()
    if (!content) throw new Error('AI 导游暂未返回有效内容')
    return {
      status: 'completed',
      taskId: String(data.task_id || id),
      content,
      revision: Number.isFinite(Number(data.revision)) ? Number(data.revision) : null,
      cached: data.cached === true
    }
  }

  async submitFeedback(taskId, {
    category,
    description = '',
    guideRevision = null,
    clientRequestId = '',
    shareId = '',
    activity = '',
    channel = this.channel,
    signal
  } = {}) {
    const id = String(taskId || '').trim()
    if (!id) throw new Error('缺少模型任务 ID')
    const normalizedCategory = String(category || '').trim()
    const categories = new Set(['content_error', 'recognition_error', 'incomplete', 'other'])
    if (!categories.has(normalizedCategory)) throw new Error('请选择有效的反馈类型')
    const normalizedDescription = String(description || '').trim()
    if (normalizedDescription.length > 2000) throw new Error('反馈说明不能超过 2000 字')
    if (normalizedCategory === 'other' && !normalizedDescription) throw new Error('请填写具体问题')

    const hasRevision = guideRevision !== null && guideRevision !== undefined && guideRevision !== ''
    const revision = hasRevision ? Number(guideRevision) : null
    const requestId = String(clientRequestId || '').trim()
    const normalizedChannel = channel === 'miniapp' ? 'miniapp' : 'web'
    const query = this.buildQuery({ shareId, activity, channel: normalizedChannel })
    const response = await fetch(joinUrl(this.baseUrl, `/api/task/${encodeURIComponent(id)}/ai-guide/feedback${query}`), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {})
      },
      body: JSON.stringify({
        category: normalizedCategory,
        ...(normalizedDescription ? { description: normalizedDescription } : {}),
        ...(Number.isInteger(revision) && revision >= 0 ? { guide_revision: revision } : {}),
        ...(requestId ? { client_request_id: requestId } : {}),
        channel: normalizedChannel
      }),
      signal
    })
    const payload = await response.json().catch(() => null)
    if (!response.ok || payload?.success === false) {
      const error = new Error(readError(payload, response.status))
      error.statusCode = response.status
      error.code = payload?.code || payload?.error?.code || ''
      error.requestId = payload?.request_id || ''
      throw error
    }
    const data = payload?.data && typeof payload.data === 'object' ? payload.data : payload || {}
    return {
      feedbackId: String(data.feedback_id || data.feedbackId || ''),
      status: String(data.status || 'pending'),
      duplicated: data.duplicated === true
    }
  }
}

export function createAiGuideFeedbackRequestId() {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
    const random = Math.random() * 16 | 0
    const value = character === 'x' ? random : (random & 0x3) | 0x8
    return value.toString(16)
  })
}

function normalizeModelFormat(value) {
  const normalized = String(value || '').trim().toLowerCase()
  return ['sog', 'ply', 'tar'].includes(normalized) ? normalized : ''
}

export function resolveAvailableModelFormats(task) {
  const rawFormats = task?.model_formats ?? task?.modelFormats
  if (!Array.isArray(rawFormats)) return []
  return [...new Set(rawFormats.map(normalizeModelFormat).filter(Boolean))]
}

export function resolveModelFormatCandidates(task, requestedFormat = '', pipelineHint = '') {
  const pipeline = String(task.pipeline_type || task.pipelineType || pipelineHint || '').toLowerCase()
  const configured = normalizeModelFormat(task.model_format || task.modelFormat || requestedFormat)
  const available = resolveAvailableModelFormats(task)

  if (pipeline === 'ai_gen_object') {
    if (!available.length) return ['ply']
    return [
      ...(available.includes('sog') ? ['sog'] : []),
      ...(available.includes('ply') ? ['ply'] : []),
      ...available.filter(format => !['sog', 'ply'].includes(format))
    ]
  }

  if (pipeline === 'direct_upload') {
    if (configured && (!available.length || available.includes(configured))) return [configured]
    return [available[0] || configured || 'sog']
  }

  if (!available.length) return ['sog', 'ply']
  return [
    ...(available.includes('sog') ? ['sog'] : []),
    ...(available.includes('ply') ? ['ply'] : []),
    ...available.filter(format => !['sog', 'ply'].includes(format))
  ]
}

export function resolveModelFormat(task, requestedFormat = '', pipelineHint = '') {
  return resolveModelFormatCandidates(task, requestedFormat, pipelineHint)[0] || 'sog'
}

function readTaskPreview(task) {
  const values = [
    task.preview_image_url,
    task.previewImageUrl,
    task.preview_image,
    task.previewImage,
    task.preview,
    task.cover_url,
    task.coverUrl,
    task.cover_image,
    task.coverImage,
    task.cover,
    task.thumbnail_url,
    task.thumbnailUrl,
    task.thumbnail,
    task.poster_url,
    task.posterUrl
  ]
  for (const value of values) {
    if (value && typeof value === 'object') {
      const nested = value.url || value.signed_url || value.signedUrl || value.src || value.path
      if (String(nested || '').trim()) return String(nested).trim()
    } else if (String(value || '').trim()) {
      return String(value).trim()
    }
  }
  return ''
}

export function mapTask(task, fallbackTitle = '') {
  const description = String(
    task.user_object_description ||
    task.userObjectDescription ||
    task.object_description ||
    task.objectDescription ||
    task.description ||
    task.summary ||
    ''
  ).trim()
  return {
    taskId: String(task.task_id || task.taskId || ''),
    title: String(task.task_name || task.taskName || task.main_object || fallbackTitle || '3D 空间'),
    description,
    author: String(task.nickname || task.owner_username || task.ownerUsername || 'MeTop 用户'),
    avatar: String(
      task.headimg || task.headimg_url || task.headimgUrl ||
      task.author_avatar || task.authorAvatar || task.avatar ||
      task.avatar_url || task.avatarUrl || ''
    ).trim(),
    preview: readTaskPreview(task),
    likes: Number(task.likes_count || task.likesCount || task.likeCount || 0),
    isLiked: Boolean(task.is_liked || task.isLike || task.isLiked),
    views: Number(task.views_count || task.viewCount || 0),
    createdAt: task.created_at || task.createdAt || '',
    pipeline: String(task.pipeline_type || task.pipelineType || '3dgs'),
    modelFormat: String(task.model_format || task.modelFormat || ''),
    lodManifestUrl: String(task.lod_manifest_url || task.lodManifestUrl || ''),
    arProjectionAvailable: supportsArProjection(task)
  }
}
